import React from 'react';
import { Clock, TrendingUp, GitBranch, ArrowRight, BarChart3, Scatter, Timeline, AlertTriangle } from 'lucide-react';

const chartOptions = [
  { 
    key: 'leadtime', 
    label: 'Lead Time', 
    description: 'Analise o tempo de ciclo dos seus itens',
    details: 'Scatterplot, histograma e estatísticas detalhadas',
    icon: Clock,
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'from-blue-50 to-indigo-50',
    borderColor: 'border-blue-200',
    hoverColor: 'hover:from-blue-100 hover:to-indigo-100',
    selectedColor: 'bg-gradient-to-r from-blue-600 to-indigo-600'
  },
  { 
    key: 'throughput', 
    label: 'Throughput', 
    description: 'Visualize a quantidade de entregas por período',
    details: 'Gráficos de barras e análise de tendências',
    icon: TrendingUp,
    color: 'from-green-500 to-teal-500',
    bgColor: 'from-green-50 to-teal-50',
    borderColor: 'border-green-200',
    hoverColor: 'hover:from-green-100 hover:to-teal-100',
    selectedColor: 'bg-gradient-to-r from-green-600 to-teal-600'
  },
  { 
    key: 'roadmap', 
    label: 'Roadmap', 
    description: 'Crie e visualize roadmaps de produto',
    details: 'Timeline interativa e gestão de marcos',
    icon: GitBranch,
    color: 'from-purple-500 to-pink-500',
    bgColor: 'from-purple-50 to-pink-50',
    borderColor: 'border-purple-200',
    hoverColor: 'hover:from-purple-100 hover:to-pink-100',
    selectedColor: 'bg-gradient-to-r from-purple-600 to-pink-600'
  },
  { 
    key: 'bloqueios', 
    label: 'Bloqueios', 
    description: 'Analise bloqueios e impedimentos',
    details: 'Visualização de tempo e categorias de bloqueios',
    icon: AlertTriangle,
    color: 'from-red-500 to-orange-500',
    bgColor: 'from-red-50 to-orange-50',
    borderColor: 'border-red-200',
    hoverColor: 'hover:from-red-100 hover:to-orange-100',
    selectedColor: 'bg-gradient-to-r from-red-600 to-orange-600'
  },
];

function ChartTypeSelector({ selected, onSelect }) {
  return (
    <div className="flex flex-col items-center gap-4 sm:gap-6 lg:gap-8 w-full max-w-6xl mx-auto mt-4 sm:mt-12 lg:mt-20 mb-4 sm:mb-12 px-4">
      <div className="text-center space-y-2 sm:space-y-3">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Escolha o tipo de gráfico</h2>
      </div>
      
      {/* Mobile: Stack cards vertically with compact design */}
      <div className="block sm:hidden w-full max-w-sm mx-auto space-y-2 px-2">
        {chartOptions.map(opt => {
          const Icon = opt.icon;
          const isSelected = selected === opt.key;
          
          return (
            <button
              key={opt.key}
              onClick={() => onSelect(opt.key)}
              className={`group relative w-full p-4 rounded-xl border-2 transition-all duration-300 text-left focus:outline-none focus:ring-4 focus:ring-blue-500/20 flex items-center gap-4 transform hover:scale-[1.02] active:scale-[0.98] min-h-[85px] ${
                isSelected 
                  ? `${opt.selectedColor} text-white border-transparent shadow-lg shadow-blue-500/25` 
                  : `bg-gradient-to-br ${opt.bgColor} ${opt.borderColor} hover:shadow-lg hover:shadow-gray-200/50 ${opt.hoverColor} hover:border-gray-300`
              }`}
            >
              {/* Compact Icon with pulse animation */}
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 ${
                isSelected 
                  ? 'bg-white/20 animate-pulse' 
                  : `bg-gradient-to-r ${opt.color} group-hover:shadow-md`
              }`}>
                <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-white'} transition-transform group-hover:scale-110`} />
              </div>
              
              {/* Enhanced Content with better text display */}
              <div className="flex-1 min-w-0 flex flex-col justify-center space-y-1">
                <h3 className={`text-base font-semibold transition-all ${isSelected ? 'text-white' : 'text-gray-900 group-hover:text-gray-700'}`}>
                  {opt.label}
                </h3>
                <p className={`text-sm leading-relaxed transition-all ${isSelected ? 'text-white/90' : 'text-gray-600 group-hover:text-gray-500'}`}>
                  {opt.description}
                </p>
              </div>
              
              {/* Enhanced Arrow with bounce animation */}
              <ArrowRight className={`w-5 h-5 flex-shrink-0 self-center transition-all duration-300 ${
                isSelected 
                  ? 'text-white translate-x-1 animate-bounce' 
                  : 'text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 group-hover:scale-110'
              }`} />
              
              {/* Enhanced selection indicator */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              )}
              
              {/* Click ripple effect */}
              <div className="absolute inset-0 rounded-lg opacity-0 group-active:opacity-20 bg-gradient-to-r from-blue-500 to-purple-500 transition-opacity duration-150"></div>
            </button>
          );
        })}
      </div>

      {/* Desktop: Enhanced interactive grid layout */}
      <div className="hidden sm:grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-3xl">
        {chartOptions.map(opt => {
          const Icon = opt.icon;
          const isSelected = selected === opt.key;
          
          return (
            <button
              key={opt.key}
              onClick={() => onSelect(opt.key)}
              className={`group relative p-4 rounded-xl border-2 transition-all duration-300 text-left focus:outline-none focus:ring-4 focus:ring-blue-500/20 transform hover:scale-105 hover:-translate-y-1 active:scale-95 ${
                isSelected 
                  ? `${opt.selectedColor} text-white border-transparent shadow-xl shadow-blue-500/25 scale-105` 
                  : `bg-gradient-to-br ${opt.bgColor} ${opt.borderColor} hover:shadow-xl hover:shadow-gray-200/50 hover:scale-105 ${opt.hoverColor} hover:border-gray-300`
              }`}
            >
              {/* Enhanced Icon with glow effect */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 ${
                isSelected 
                  ? 'bg-white/20 shadow-lg animate-pulse' 
                  : `bg-gradient-to-r ${opt.color} group-hover:shadow-lg`
              }`}>
                <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-white'} transition-all duration-300 group-hover:scale-110`} />
              </div>
              
              {/* Enhanced Content */}
              <div className="space-y-1 mb-3">
                <h3 className={`text-lg font-semibold transition-all duration-300 ${isSelected ? 'text-white' : 'text-gray-900 group-hover:text-gray-700'}`}>
                  {opt.label}
                </h3>
                <p className={`text-xs transition-all duration-300 ${isSelected ? 'text-white/90' : 'text-gray-600 group-hover:text-gray-500'}`}>
                  {opt.description}
                </p>
                <p className={`text-xs font-medium transition-all duration-300 ${isSelected ? 'text-white/85' : 'text-gray-700 group-hover:text-gray-600'}`}>
                  {opt.details}
                </p>
              </div>
              
              {/* Enhanced Arrow with interactive states */}
              <div className={`flex items-center gap-2 text-sm font-medium transition-all duration-300 ${
                isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'
              }`}>
                <span className="transition-all duration-300">{isSelected ? 'Selecionado' : 'Selecionar'}</span>
                <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                  isSelected ? 'translate-x-1 animate-bounce' : 'group-hover:translate-x-2 group-hover:scale-110'
                }`} />
              </div>
              
              {/* Enhanced selection indicator */}
              {isSelected && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              )}
              
              {/* Hover glow effect */}
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-10 transition-all duration-300 ${
                isSelected ? 'bg-white' : 'bg-gradient-to-r from-blue-500 to-purple-500'
              }`}></div>
              
              {/* Click ripple effect */}
              <div className="absolute inset-0 rounded-xl opacity-0 group-active:opacity-20 bg-gradient-to-r from-blue-500 to-purple-500 transition-opacity duration-150"></div>
              
              {/* Floating shadow enhancement */}
              <div className={`absolute inset-0 rounded-xl transition-all duration-300 ${
                isSelected 
                  ? 'shadow-2xl shadow-blue-500/30' 
                  : 'group-hover:shadow-2xl group-hover:shadow-gray-300/30'
              } -z-10`}></div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ChartTypeSelector;
